/**
 * De um erro para um dos cinco estados (H03, §11.1).
 *
 * ── Por que a decisão é uma função pura, e não um `if` na tela ────────────
 *
 * Porque ela é a mesma em quinze telas e erra sempre do mesmo jeito quando é
 * copiada: alguém mostra "tentar de novo" num 400, alguém trata 404 como erro
 * genérico e revela que o recurso existe, alguém trata `ENGINE_INDISPONIVEL`
 * como falha e trava a tela inteira.
 *
 * Aqui a decisão é dado e tem teste. A tela recebe o veredito pronto.
 */

import { ErroDaApi, ErroDeRede } from '@cliqcrm/contracts';

export type Veredito =
  /** Erro comum. Mostra mensagem segura, `correlation_id` e — se couber — repetir. */
  | { readonly tipo: 'erro'; readonly podeRepetir: boolean; readonly correlationId: string | null }
  /**
   * Recurso não encontrado OU de outra revenda. A tela **não distingue**.
   *
   * A B16 devolve 404 nos dois casos, e a §19 explica por quê: 403 confirmaria
   * que o registro existe, e daria para mapear a carteira do concorrente
   * testando identificadores. Distinguir na tela desfaria a proteção do
   * servidor.
   */
  | { readonly tipo: 'sem-acesso' }
  /**
   * A operação existe e esta pessoa não pode executá-la.
   *
   * Diferente do anterior: aqui o servidor JÁ decidiu revelar — é 403 sobre uma
   * AÇÃO ("mudar a política de MFA"), não sobre um recurso. Nada é exposto que
   * o papel da pessoa não deixasse deduzir.
   */
  | { readonly tipo: 'sem-permissao' }
  /**
   * Engine fora. **Não é erro de tela** — é o modo degradado (§14.3).
   *
   * É a armadilha que a H03 nomeia: *"tratar modo degradado como erro. Com o
   * Twenty fora, a conversa ainda pode ser lida e respondida — bloquear a tela
   * inteira transforma degradação parcial em indisponibilidade total."*
   */
  | { readonly tipo: 'degradado'; readonly correlationId: string | null }
  /** Sessão caiu no meio. Quem trata é o contexto de sessão, não a tela. */
  | { readonly tipo: 'sem-sessao' };

/** Códigos do catálogo (B34) que significam "a engine está fora", não "deu erro". */
const CODIGOS_DE_DEGRADACAO = new Set(['ENGINE_INDISPONIVEL']);

export function diagnosticar(erro: unknown): Veredito {
  if (erro instanceof ErroDeRede) {
    // Sem `correlation_id`: a requisição não chegou ao servidor, então não há
    // log lá. Pedir o código ao usuário seria mandá-lo atrás de algo que não
    // existe.
    return { tipo: 'erro', podeRepetir: true, correlationId: null };
  }

  if (!(erro instanceof ErroDaApi)) {
    // Erro de programação — `TypeError`, schema que não bateu. Repetir não
    // muda nada, e prometer que muda gasta a paciência de quem clica.
    return { tipo: 'erro', podeRepetir: false, correlationId: null };
  }

  if (erro.codigo === 'NAO_AUTENTICADO') return { tipo: 'sem-sessao' };
  if (erro.codigo === 'NAO_ENCONTRADO') return { tipo: 'sem-acesso' };
  if (erro.codigo === 'SEM_PERMISSAO') return { tipo: 'sem-permissao' };

  if (CODIGOS_DE_DEGRADACAO.has(erro.codigo)) {
    return { tipo: 'degradado', correlationId: erro.correlationId };
  }

  // `retryable` sai do catálogo, e é o SERVIDOR quem diz. Decidir aqui por
  // faixa de status erraria: 429 é repetível e 409 de idempotência em curso
  // também, e nenhum dos dois é 5xx.
  return {
    tipo: 'erro',
    podeRepetir: erro.retryable,
    correlationId: erro.correlationId,
  };
}

/**
 * Quem pode forçar a atualização de um dado defasado.
 *
 * A §14.3 pede que a tela ofereça atualizar *"apenas a perfis autorizados"*, e
 * a razão é de custo: forçar atualização vai à engine, e a engine já está
 * ruim — é o motivo de o dado estar defasado. Um botão disponível a todos numa
 * revenda de trinta vendedores viraria trinta chamadas contra quem está
 * caindo, exatamente quando ele menos aguenta.
 *
 * Quem decide manter a pressão é quem responde pela operação.
 */
const PAPEIS_QUE_PODEM_ATUALIZAR = new Set([
  'gestor',
  'admin_tenant',
  'operacao_cliqfy',
  'super_admin',
]);

export function podeForcarAtualizacao(papel: string | null | undefined): boolean {
  return papel != null && PAPEIS_QUE_PODEM_ATUALIZAR.has(papel);
}
